/*
 * Keeps a multi-gift cart from losing the gifts the shopper has not seen yet.
 *
 * A cart can earn several free gifts at once — a look whose tier grants both a
 * thong and a bralette, or two looks sharing a garment. Goalify offers them one
 * popup at a time, and closing one popup ends the whole sequence: the gifts
 * behind it are never shown, never declined, and simply do not arrive. Only a
 * page load surfaces them again, because a page load re-evaluates from scratch.
 *
 * Re-evaluating is the fix, and Goalify already exposes the trigger: it listens
 * for `SCE:update` on the document and re-runs its goals. Dispatching that after
 * a gift popup closes lets the next gift take its turn.
 *
 * Nothing here grants, prices or removes anything — the decision stays Goalify's.
 * A goal it has already settled carries its own session key and is not offered
 * twice, so a redundant dispatch costs a re-evaluation and nothing else. That is
 * why the caps below are written against cost rather than correctness.
 */

(() => {
  const POPUP = ".n99gb-fgPopup";
  const INLINE = ".n99gb-wrapper-gifts";
  const REEVALUATE = "SCE:update";

  /*
   * Long enough that focus has settled where the shopper put it before a new
   * dialog can claim it, and long enough to collapse the burst of mutations one
   * close produces.
   *
   * Also long enough that the next dialog cannot arrive under a second click
   * aimed at the last one. Both dialogs put their dismissal in the same place,
   * so a shopper clicking twice — impatiently, or because the first press did
   * not seem to take — was throwing away a gift that had been on screen for
   * seven hundred milliseconds and never read. Goalify spends about a second of
   * its own after this.
   */
  const SETTLE_MS = 1400;

  /*
   * Each dispatch costs a cart fetch and a re-render in the app's own UI. The
   * sequence ends on its own — a cart with nothing left to offer produces no
   * popup, so no further close, so no further dispatch — and this only bounds
   * the pathological case where something upstream keeps reopening.
   */
  const MAX_DISPATCHES = 8;

  /*
   * A gift the shopper has settled leaves a key behind — one spelling for taken,
   * another for turned down. Dismissing the dialog without answering leaves
   * nothing, and that difference is the whole guard below.
   */
  const RESOLVED = /_free_gift_(selected|declined)$/;

  const resolvedCount = () =>
    Object.keys(sessionStorage).filter((key) => RESOLVED.test(key)).length;

  let dispatches = 0;
  let settled = resolvedCount();
  let timer = null;

  /*
   * The controls currently on the page, so picker changes can refresh how many
   * gifts are outstanding and whether a fallback is needed.
   *
   * They are told, rather than watching for themselves. A control that watched
   * the document would see the text it writes as another change to react to,
   * and rewrite it, without end.
   */
  const live = new Set();

  const refresh = () => live.forEach((control) => control.render());

  const holdsPopup = (node) =>
    node.nodeType === 1 &&
    (node.matches?.(POPUP) || node.querySelector?.(POPUP));

  const holdsInline = (node) =>
    node.nodeType === 1 &&
    (node.matches?.(INLINE) || node.querySelector?.(INLINE));

  const inlineChanged = (mutations) =>
    mutations.some(
      (mutation) =>
        mutation.target.closest?.(INLINE) ||
        (mutation.type === "attributes" && holdsInline(mutation.target)) ||
        Array.prototype.some.call(mutation.addedNodes, holdsInline) ||
        Array.prototype.some.call(mutation.removedNodes, holdsInline),
    );

  const hasInlinePicker = (control) => {
    const drawer = control.closest("#CartDrawer");
    if (!drawer) return false;

    return Array.from(
      drawer.querySelectorAll(`${INLINE} .n99gb-giftButton:not(:disabled)`),
    ).some((button) => {
      // Drawer visibility changes when it closes. Only hiding within the
      // drawer makes a picker unavailable after the shopper opens it again.
      for (
        let node = button;
        node && node !== drawer;
        node = node.parentElement
      ) {
        if (getComputedStyle(node).display === "none") return false;
      }
      return true;
    });
  };

  /*
   * The popup is torn out inside a generic overlay wrapper that other popups on
   * the storefront share, so the gift popup is identified by its own class
   * somewhere in the removed subtree rather than by the wrapper.
   */
  const closed = (mutations) =>
    mutations.some((mutation) =>
      Array.prototype.some.call(mutation.removedNodes, holdsPopup),
    );

  /*
   * The listener is on the window, not the document — dispatching only to the
   * document is silently ignored, which looks identical to the bug being
   * unfixable. Both are dispatched because the app's own code reaches for either
   * target depending on the surface, and a redundant one is inert.
   */
  const reevaluate = ({ force = false } = {}) => {
    /*
     * Only a dialog the shopper answered earns another one. Closing without
     * answering settles nothing, so re-evaluating would rebuild the very dialog
     * that was just dismissed — and keep rebuilding it, since each rebuild can
     * be dismissed the same way. Someone who wants no gift has to be able to
     * say so by closing.
     *
     * Asking for the picker outright is exempt: there the dialog is what was
     * requested.
     */
    const nowSettled = resolvedCount();
    if (!force && nowSettled <= settled) return;
    settled = nowSettled;

    if (dispatches >= MAX_DISPATCHES) return;
    dispatches += 1;
    window.dispatchEvent(new CustomEvent(REEVALUATE, { detail: {} }));
    document.dispatchEvent(new CustomEvent(REEVALUATE, { detail: {} }));
  };

  /*
   * Says which gift of how many is on screen.
   *
   * The picker offers one gift at a time and says nothing about the rest, so a
   * shopper choosing a thong has no way to know a bralette follows — and one
   * who does not want the thong has no reason to expect anything else.
   *
   * The counting is the drawer's, not this dialog's: the control there carries
   * what the cart earns, and what is still unsettled is already worked out for
   * its own label. Marked once per dialog, the way the swatches are, because the
   * dialog is React and rebuilds itself.
   */
  const MARKED = "data-umi-step";

  const annotate = () => {
    const popup = document.querySelector(`${POPUP}:not([${MARKED}])`);
    if (!popup) return;

    const control = document.querySelector("gift-earned");
    const total = Number(control?.dataset.earnedTotal) || 0;
    const copy = control?.dataset.copyStep;
    popup.setAttribute(MARKED, "true");
    if (total < 2 || !copy) return;

    const current = Math.min(resolvedCount() + 1, total);
    const header = popup.querySelector(".n99gb-fgPopup-header");

    const line = document.createElement("p");
    line.className = "gift-step";
    line.textContent = copy.replace("#", current).replace("~", total);

    /*
     * Below the header, never inside it. That row does not wrap and holds the
     * title and the close control, so a third thing put there lands between
     * them and folds the title in half.
     */
    if (header) header.insertAdjacentElement("afterend", line);
    else popup.prepend(line);
  };

  const start = () => {
    // Absent on any page the app has not loaded on, and absent entirely if the
    // app is removed — in either case there is nothing to keep alive.
    if (!window.N99GB_goalsChunk1) return;

    /*
     * Inline changes refresh the fallback without advancing the popup queue:
     * replacing a drawer can remove a picker without the shopper answering it.
     * Only picker mutations are considered, avoiding the controls' own text.
     */
    new MutationObserver((mutations) => {
      annotate();

      if (inlineChanged(mutations)) refresh();

      if (!closed(mutations)) return;
      refresh();
      clearTimeout(timer);
      timer = setTimeout(() => reevaluate(), SETTLE_MS);
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["disabled", "class", "style", "hidden"],
    });

    annotate();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  /*
   * The control the drawer offers for choosing a gift the cart has earned.
   *
   * Liquid counts what the cart earns but cannot tell what is left to choose: a
   * gift taken becomes an ordinary line, and a gift turned down leaves the cart
   * untouched altogether. Both are settled here, so the count is finished here
   * and the control hides itself once nothing is outstanding.
   *
   * Pressing it asks for the same re-evaluation the queue uses. That is the only
   * way to reach the picker that always works — waiting for it to arrive by
   * itself is what loses gifts — and because the shopper asks, the dialog is an
   * answer rather than an interruption.
   */
  if (!customElements.get("gift-earned")) {
    customElements.define(
      "gift-earned",
      class GiftEarned extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector("button");
          this.label = this.querySelector("[data-gift-earned-label]");
          this.button?.addEventListener("click", this.onClick);
          this.render();
          live.add(this);
        }

        disconnectedCallback() {
          live.delete(this);
          this.button?.removeEventListener("click", this.onClick);
        }

        onClick = () => {
          reevaluate({ force: true });
        };

        get outstanding() {
          const earned = Number(this.dataset.earned) || 0;
          return earned - resolvedCount();
        }

        render() {
          const left = this.outstanding;
          const inline = hasInlinePicker(this);
          this.toggleAttribute("hidden", left < 1 || !!inline);
          if (left < 1 || inline || !this.label) return;

          if (left === 1) {
            this.label.textContent = this.dataset.copyOne || "";
            return;
          }
          const marker = this.dataset.copyCountMarker || "#";
          this.label.textContent = (this.dataset.copyMany || "").replace(
            marker,
            left,
          );
        }
      },
    );
  }
})();
